// client/src/components/Parent/ChildPicker.js
//
// WHOSE DATA THE PAGE IS SHOWING, and a way to change it only when there is
// something to change to.
//
// A dropdown with one option is not a choice, it is a control that looks like
// one — so with a single child the select is gone and the child is simply named.
//
// NAMED, not hidden. Dropping the whole block for one child was the obvious
// reading of "no selector needed", and it is wrong for the same reason it was
// wrong on the phone (see apps/mobile/src/components/ChildPicker.js, which
// carries this scar already): every parent page would then display marks, fees
// and attendance with nothing on screen saying who they belong to. For a parent
// with one child that is merely bare; the moment a second child is added it is
// dangerous, because the page changes shape and the reader has no anchor.
//
// So: one child gets an identity card, several get the same card plus a select.
// The four parent pages each had their own copy of that dropdown, which is why
// they had four slightly different ideas of how to name a student.

import React from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import StudentAvatar from './StudentAvatar';
import { usePhase } from '../../contexts/PhaseContext';

/** The id a page keys its fetches on. Children carry both spellings. */
export const childId = (child) => child?.studentId || child?.id;

/** "Ada Nkeng", or a fallback that at least identifies the record. */
export function childName(child, fallbackWord = 'Student') {
  const name = `${child?.firstName || ''} ${child?.lastName || ''}`.trim();
  return name || `${fallbackWord} ${childId(child) ?? ''}`.trim();
}

/**
 * Class, section, and student number: what tells siblings apart when names do
 * not.
 *
 * THE SECTION ONLY WHEN IT DISTINGUISHES SOMETHING. A family whose children are
 * all in one section learns nothing from being told so on every row, and it is
 * a word most parents have no use for. A family spanning both needs it, because
 * "Class 2A" and "Form 1" mean nothing to somebody who does not already know
 * which section each belongs to.
 *
 * Same rule the picker already applies to itself: one option is not a choice,
 * and one section is not a distinction.
 */
export const childDetail = (child, sectionLabel = null) =>
  [child?.className, sectionLabel, child?.studentNumber].filter(Boolean).join(' • ');

/** Does this family sit in more than one section? */
export const spansSections = (list) =>
  new Set((Array.isArray(list) ? list : []).map((c) => c?.phaseCode).filter(Boolean)).size > 1;

export default function ChildPicker({
  children: list,
  value,
  onChange,
  label,
  studentWord = 'Student',
  sx,
}) {
  const { getPhaseLabel } = usePhase();
  const rows = Array.isArray(list) ? list : [];
  if (!rows.length) return null;

  const selected = rows.find((c) => String(childId(c)) === String(value)) || rows[0];
  const multiple = rows.length > 1;

  // "Nursery & Primary" / "Secondary" — the school's own words for a section,
  // reused from the phase context so the parent portal cannot name them
  // differently from the rest of the app. The parent never sees the word
  // "phase". Null unless the family actually spans both.
  const spans = spansSections(rows);
  const sectionOf = (c) => (spans && c?.phaseCode ? getPhaseLabel(c.phaseCode) : null);

  const card = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <StudentAvatar student={selected} size={48} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h6" sx={{ lineHeight: 1.2 }} noWrap>
          {childName(selected, studentWord)}
        </Typography>
        {childDetail(selected, sectionOf(selected)) ? (
          <Typography variant="body2" color="text.secondary" noWrap>
            {childDetail(selected, sectionOf(selected))}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );

  if (!multiple) return <Box sx={{ mb: 3, ...sx }}>{card}</Box>;

  return (
    <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', ...sx }}>
      {card}
      <FormControl sx={{ minWidth: 260, flex: 1 }}>
        <InputLabel>{label}</InputLabel>
        <Select value={value ?? ''} label={label} onChange={(e) => onChange?.(e.target.value)}>
          {rows.map((c) => {
            const id = childId(c);
            const detail = childDetail(c, sectionOf(c));
            return (
              <MenuItem key={id} value={id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <StudentAvatar student={c} size={28} />
                  <span>{detail ? `${childName(c, studentWord)} • ${detail}` : childName(c, studentWord)}</span>
                </Box>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
}
